import './component.scss'
import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'
import '@splidejs/splide/css';
import { Splide } from '@splidejs/splide';

import './images/pointer.svg'
import './images/city.svg'
import './images/arrow_down_gray.svg'
import './images/white_arrow_right.svg'

import 'Components/refosumu/subsidies/search_supported_client/simple_company_card/component'

const BREAKPOINT = 768
const app = Application.start()

app.register(
  'search-supported-client',
  class extends Controller {
    static targets = ["prefectureSelect", "PrefectureIcon", "PrefectureArrow", "citySelect", "cityIcon", "cityArrow", "companiesArea", "companiesList", "noCompanies"]
    static values = { subsidyId: Number, subsidyType: String }

    // Splideインスタンスを管理
    splideInstance = null
    // 元のHTMLを保持（リサイズ時の再構築用）
    originalHtml = null

    connect() {
      this.resizeHandler = this.handleResize.bind(this)
      window.addEventListener('resize', this.resizeHandler)
    }

    disconnect() {
      this.destroySplide()
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
      }
    }

    prefectureChanged() {
      const selectElement = this.prefectureSelectTarget
      const iconElement = this.PrefectureIconTarget
      const arrowElement = this.PrefectureArrowTarget
      const prefectureId = selectElement.value

      selectElement.classList.add('search-supported-client__select--selected')
      iconElement.classList.add('search-supported-client__select-icon--selected')
      arrowElement.classList.add('search-supported-client__select-arrow--selected')

      this.clearCityOptions()
      this.updateCities(prefectureId)
      this.updateCompanies(prefectureId, null)
    }

    cityChanged() {
      const citySelectElement = this.citySelectTarget
      const cityIconElement = this.cityIconTarget
      const cityArrowElement = this.cityArrowTarget
      const prefectureId = this.prefectureSelectTarget.value
      const cityId = citySelectElement.value

      // デフォルト値である'市区町村'のoptionが選択された場合は市区町村選択欄の状態をリセットする
      if (cityId === '') {
        this.clearCityOptions()
        this.updateCities(prefectureId)
        this.updateCompanies(prefectureId, null)
        return
      }

      citySelectElement.classList.add('search-supported-client__select--selected')
      cityIconElement.classList.add('search-supported-client__select-icon--selected')
      cityArrowElement.classList.add('search-supported-client__select-arrow--selected')

      this.updateCompanies(prefectureId, cityId)
    }

    async updateCities(prefectureId) {
      const citiesData = await this.fetchSubsidySupportedCities(prefectureId)
      const citySelect = this.citySelectTarget

      const hasCities = citiesData.cities && citiesData.cities.length > 0
      const hasAnySupportedClients = hasCities && citiesData.cities.some(city => city.has_supported_clients)

      if (hasCities && hasAnySupportedClients) {
        citiesData.cities.forEach(city => {
          const option = document.createElement('option')
          option.value = city.id
          option.textContent = city.name

          citySelect.appendChild(option)
        })
      } else {
        this.disableCitySelect()
      }
    }

    disableCitySelect() {
      const citySelect = this.citySelectTarget
      const citySelectItem = citySelect.closest('.search-supported-client__select-item')

      citySelect.disabled = true
      citySelect.classList.add('search-supported-client__select--disabled')
      citySelectItem.classList.add('search-supported-client__select-item--disabled')
    }

    enableCitySelect() {
      const citySelect = this.citySelectTarget
      const citySelectItem = citySelect.closest('.search-supported-client__select-item')

      citySelect.disabled = false
      citySelect.classList.remove('search-supported-client__select--disabled')
      citySelectItem.classList.remove('search-supported-client__select-item--disabled')
    }

    showNoCompaniesMessage() {
      const noCompaniesElement = this.noCompaniesTarget
      noCompaniesElement.style.display = 'block'
    }

    hideNoCompaniesMessage() {
      const noCompaniesElement = this.noCompaniesTarget
      noCompaniesElement.style.display = 'none'
    }

    async fetchSubsidySupportedCities(prefectureId) {
      const params = new URLSearchParams({
        subsidy_id: this.subsidyIdValue,
        subsidy_type: this.subsidyTypeValue,
        prefecture_id: prefectureId
      })

      const response = await fetch(`/api/subsidy_supported_client_cities?${params}`)
      return response.json()
    }

    async updateCompanies(prefectureId, cityId) {
      const companiesData = await this.fetchSupportedClients(prefectureId, cityId)
      const html = companiesData.html
      const companiesArea = this.companiesAreaTarget
      const companiesList = this.companiesListTarget
      const isCompanyFound = Boolean(html && html.trim() !== '')

      if (isCompanyFound) {
        this.destroySplide()
        this.originalHtml = html
        this.renderByScreenSize()
        this.initializeSliderIfNeeded()
        companiesArea.style.display = 'block'
        this.hideNoCompaniesMessage()
      } else {
        this.destroySplide()
        this.originalHtml = null
        companiesList.innerHTML = ''
        companiesArea.style.display = 'none'
        this.showNoCompaniesMessage()
      }

      this.sendGA4Event(prefectureId, cityId, isCompanyFound)
    }

    async fetchSupportedClients(prefectureId, cityId) {
      const params = new URLSearchParams({
        subsidy_id: this.subsidyIdValue,
        subsidy_type: this.subsidyTypeValue,
        prefecture_id: prefectureId
      })

      if (cityId) {
        params.append('city_id', cityId)
      }

      const response = await fetch(`/api/subsidy_supported_clients?${params}`)
      return response.json()
    }

    clearCityOptions() {
      const citySelect = this.citySelectTarget
      citySelect.innerHTML = ''

      const defaultOption = document.createElement('option')
      defaultOption.value = ''
      defaultOption.selected = true
      defaultOption.textContent = '市区町村'

      citySelect.appendChild(defaultOption)

      this.enableCitySelect()
      this.hideNoCompaniesMessage()
      this.citySelectTarget.classList.remove('search-supported-client__select--selected')
      this.cityIconTarget.classList.remove('search-supported-client__select-icon--selected')
      this.cityArrowTarget.classList.remove('search-supported-client__select-arrow--selected')
    }

    sendGA4Event(prefectureId, cityId, isClientFound) {
      window.dataLayer = window.dataLayer || []
      const now = new Date()
      const currentTime = now.toISOString().slice(0, 19).replace('T', ' ')

      window.dataLayer.push({
        event: 'subsidySupportClientSearchClick',
        subsidySupportClientSearchPrefectureId: prefectureId,
        subsidySupportClientSearchCityId: cityId || null,
        subsidySupportClientSearchFound: isClientFound,
        subsidySupportClientSearchClickTime: currentTime
      })
    }

    renderByScreenSize() {
      if (!this.originalHtml) return

      const companiesList = this.companiesListTarget
      const cardCount = this.getCardCountFromHtml(this.originalHtml)

      if (window.innerWidth <= BREAKPOINT && cardCount >= 2) {
        companiesList.classList.add('splide')

        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.originalHtml
        const companyItems = tempDiv.children

        Array.from(companyItems).forEach(item => {
          item.classList.add('splide__slide')
        })

        const splideHtml = `
          <div class="splide__track">
            <div class="splide__list">
              ${tempDiv.innerHTML}
            </div>
          </div>
        `

        companiesList.innerHTML = splideHtml
      } else {
        companiesList.classList.remove('splide')
        companiesList.innerHTML = this.originalHtml
      }
    }

    getCardCountFromHtml(html) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.querySelectorAll('.simple-company-card').length
    }

    initializeSliderIfNeeded() {
      const cardCount = this.getCardCountFromHtml(this.originalHtml || '')

      if (window.innerWidth <= BREAKPOINT && cardCount >= 2) {
        this.initializeSplide()
      }
    }

    initializeSplide() {
      const companiesList = this.companiesListTarget

      if (!companiesList || !companiesList.classList.contains('splide')) return

      const config = {
        speed: 800,
        perMove: 1,
        perPage: 1,
        gap: '12px',
        focus: 'center'
      }

      this.splideInstance = new Splide(companiesList, config)
      this.splideInstance.mount()
    }

    destroySplide() {
      if (this.splideInstance) {
        this.splideInstance.destroy()
        this.splideInstance = null
      }

      const companiesList = this.companiesListTarget
      if (companiesList) {
        companiesList.classList.remove('splide')
      }
    }

    handleResize() {
      if (!this.originalHtml) return

      this.destroySplide()
      this.renderByScreenSize()
      this.initializeSliderIfNeeded()
    }
  }
)
